import { Injectable, effect } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { IClient, ISession, State, getStateLabelByNumber } from 'common_library';
import { TranslateService } from '@ngx-translate/core';
import { MainStateService } from 'src/app/services/state/app-main-state.service';
import { TranslateConfigService } from 'src/app/services/translate.service';
import { ChargingSessionLive } from './charging-session-live.plugin';
import { StartChargingSessionOptions, UpdateChargingSessionOptions } from './charging-session-live.types';
import { HttpIoService } from 'src/app/services/communication/http-io.service';

const UPDATE_MIN_INTERVAL_MS = 10000;

@Injectable({ providedIn: 'root' })
export class ChargingLiveService {
  private activeSessionId: string | null = null;
  private lastUpdateAt = 0;
  private lastPayloadSignature = '';
  private pluginUnavailableLogged = false;

  constructor(
    private mainStateService: MainStateService,
    private translate: TranslateService,
    private translateConfigService: TranslateConfigService,
    private HIO: HttpIoService,
  ) {
    console.log('[ChargingLive] service init', JSON.stringify({
      platform: Capacitor.getPlatform(),
      native: Capacitor.isNativePlatform(),
      pluginAvailable: this.isPluginAvailable(),
      mode: 'iOS uses ActivityKit: Dynamic Island when supported, otherwise Lock Screen Live Activity',
    }));

    effect(() => {
      const native = this.mainStateService.$native();
      const sessions = this.mainStateService.$sessions() || [];

      if (!native) {
        console.log('[ChargingLive] sync skipped: not native platform');
        return;
      }
      this.syncFromSessions(sessions);
    });

    if (this.isPluginAvailable()) {
      // iOS: receive Live Activity push token and forward it to the backend.
      // This enables server-side APNs updates — works even when the app is killed.
      ChargingSessionLive.addListener('pushTokenUpdate', ({ pushToken, sessionId }) => {
        console.log('[ChargingLive] pushTokenUpdate received', JSON.stringify({
          sessionId,
          tokenLength: pushToken?.length || 0,
        }));
        this.reportLiveActivityPushToken(pushToken, sessionId)
          .catch((error: unknown) => console.warn('[ChargingLive] reportLiveActivityPushToken failed', error));
      }).catch((error: unknown) => console.warn('[ChargingLive] addListener pushTokenUpdate failed', error));
    } else {
      console.warn('[ChargingLive] plugin unavailable at service init');
    }
  }

  async reconcile(): Promise<void> {
    if (!this.mainStateService.getNative()) {
      console.log('[ChargingLive] reconcile skipped: not native');
      return;
    }
    console.log('[ChargingLive] reconcile requested');
    await this.syncFromSessions(this.mainStateService.getSessions() || [], true);
  }

  private async syncFromSessions(sessions: ISession[], force = false): Promise<void> {
    if (!this.isPluginAvailable()) {
      if (!this.pluginUnavailableLogged) {
        console.warn('[ChargingLive] sync skipped: ChargingSessionLive plugin unavailable', JSON.stringify({
          platform: Capacitor.getPlatform(),
          native: Capacitor.isNativePlatform(),
        }));
        this.pluginUnavailableLogged = true;
      }
      return;
    }

    const liveSession = this.pickLiveSession(sessions);
    console.log('[ChargingLive] sync', JSON.stringify({
      sessions: sessions.length,
      activeSessionId: this.activeSessionId,
      force,
      selected: liveSession ? this.describeSession(liveSession) : null,
    }));

    if (!liveSession) {
      if (this.activeSessionId) {
        console.log('[ChargingLive] no live session: stopping tracked native session', this.activeSessionId);
        await this.safeStop(this.activeSessionId);
      } else {
        const nativeState = await this.safeGetActiveSession();
        console.log('[ChargingLive] no live session: native active state', JSON.stringify(nativeState));
        if (nativeState.sessionId) await this.safeStop(nativeState.sessionId);
      }
      this.activeSessionId = null;
      this.lastPayloadSignature = '';
      return;
    }

    const payload = this.toPayload(liveSession);
    const signature = JSON.stringify(payload);
    const now = Date.now();
    const shouldStart = force || this.activeSessionId !== liveSession.id;
    const shouldUpdate = shouldStart || signature !== this.lastPayloadSignature;
    const throttled = !shouldStart && now - this.lastUpdateAt < UPDATE_MIN_INTERVAL_MS;

    console.log('[ChargingLive] decision', JSON.stringify({
      sessionId: liveSession.id,
      shouldStart,
      shouldUpdate,
      throttled,
      elapsedSinceLastUpdateMs: now - this.lastUpdateAt,
      payload: this.describePayload(payload),
    }));

    if (!shouldUpdate || throttled) {
      console.log('[ChargingLive] sync skipped by decision', JSON.stringify({
        sessionId: liveSession.id,
        reason: !shouldUpdate ? 'unchangedPayload' : 'throttled',
      }));
      return;
    }

    if (shouldStart) {
      try {
        const perm = await ChargingSessionLive.requestPermissions().catch(() => ({ granted: false }));
        console.log('[ChargingLive] permissions', JSON.stringify(perm));
        if (!perm.granted) {
          console.warn('[ChargingLive] start skipped: native live permission/capability not granted', JSON.stringify({
            sessionId: payload.sessionId,
          }));
          return;
        }

        console.log('[ChargingLive] start native', JSON.stringify(this.describePayload(payload)));
        await ChargingSessionLive.start(payload);
        console.log('[ChargingLive] start OK', payload.sessionId);
      } catch (err) {
        console.error('[ChargingLive] start FAILED', err);
        return;
      }
      this.activeSessionId = liveSession.id;
    } else {
      console.log('[ChargingLive] update native', JSON.stringify(this.describePayload(payload)));
      await ChargingSessionLive.update(payload)
        .catch(err => {
          console.warn('[ChargingLive] update FAILED', err);
          if ((err as { code?: string })?.code === 'UNIMPLEMENTED') this.activeSessionId = null;
        });
    }

    this.lastPayloadSignature = signature;
    this.lastUpdateAt = now;
    console.log('[ChargingLive] sync completed', JSON.stringify({
      activeSessionId: this.activeSessionId,
      lastUpdateAt: this.lastUpdateAt,
    }));
  }

  private pickLiveSession(sessions: ISession[]): ISession | null {
    const activeSessions = sessions.filter(session => !!session && !session.endedAt && !session.cancelledAt);
    if (activeSessions.length === 0) return null;

    const charging = activeSessions.find(session => session.state === State.CHARGING);
    if (charging) return charging;

    const occupied = activeSessions.find(session => session.state === State.OCCUPIED);
    if (occupied) return occupied;

    const preparing = activeSessions.find(session => session.state === State.PREPARING);
    if (preparing) return preparing;

    return activeSessions[0];
  }

  private toPayload(session: ISession): StartChargingSessionOptions & UpdateChargingSessionOptions {
    const startedAt = this.toIsoString(session.startedAt || session.reservedAt || session.createdAt);
    const stateKey = this.resolveLiveTitleKey(session);
    const language = this.translateConfigService.forceGetCurrentLanguage();
    const title = this.translate.instant(`CHARGING_LIVE.TITLE_BY_STATE.${stateKey}`);

    return {
      sessionId: session.id,
      title,
      language,
      stateKey,
      startedAt,
      kwh: this.kwh(session.energy),
      powerKw: this.kw(session.power),
      estimatedCost: this.round(session.totalCost ?? this.sumCosts(session)),
      currency: 'EUR',
      socPercent: this.toInteger(session.soc),
      elapsedSeconds: this.elapsedSeconds(session.startedAt || session.reservedAt || session.createdAt),
      occupationSeconds: this.occupationSeconds(session),
      gracePeriodSeconds: this.gracePeriodSeconds(session),
      reservationSeconds: this.reservationSeconds(session),
      canStop: session.state === State.CHARGING || session.state === State.OCCUPIED || session.state === State.PREPARING,
    };
  }

  /**
   * Picks the i18n title key accounting for OCPP innerState.
   * Finishing / SuspendedEV / SuspendedEVSE are sub-states of CHARGING.
   */
  private resolveLiveTitleKey(session: ISession): string {
    if (session.state === State.CHARGING) {
      switch (session.innerState) {
        case 'Finishing':     return 'FINISHING';
        case 'SuspendedEV':   return 'SUSPENDED_EV';
        case 'SuspendedEVSE': return 'SUSPENDED_EVSE';
      }
    }
    return getStateLabelByNumber(session.state) || 'CHARGING';
  }

  private sumCosts(session: ISession): number {
    return (session.energyCost || 0) + (session.occupationCost || 0) + (session.reservationCost || 0);
  }

  private occupationSeconds(session: ISession): number | undefined {
    if (session.occupationTime) return Math.round(session.occupationTime * 3600);
    if (session.stoppedAt && !session.endedAt) return this.elapsedSeconds(session.stoppedAt);
    return undefined;
  }

  private gracePeriodSeconds(session: ISession): number | undefined {
    if (session.innerState !== 'Finishing') return undefined;
    return this.occupationSeconds(session);
  }

  private reservationSeconds(session: ISession): number | undefined {
    if (!session.reservedAt || session.startedAt) return undefined;
    return this.elapsedSeconds(session.reservedAt);
  }

  private elapsedSeconds(date?: Date | string): number | undefined {
    if (!date) return undefined;
    const value = new Date(date).getTime();
    if (!Number.isFinite(value)) return undefined;
    return Math.max(0, Math.floor((Date.now() - value) / 1000));
  }

  private toIsoString(date?: Date | string): string {
    if (!date) return new Date().toISOString();
    const parsed = new Date(date);
    return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : new Date().toISOString();
  }

  private round(value?: number): number | undefined {
    if (value === null || value === undefined || !Number.isFinite(value)) return undefined;
    return Math.round(value * 100) / 100;
  }

  private kwh(energyWh?: number): number | undefined {
    return this.round(this.toFiniteNumber(energyWh) / 1000);
  }

  private kw(powerW?: number): number | undefined {
    return this.round(this.toFiniteNumber(powerW) / 1000);
  }

  private toFiniteNumber(value?: number): number {
    return value === null || value === undefined || !Number.isFinite(value) ? 0 : value;
  }

  private toInteger(value?: number): number | undefined {
    if (value === null || value === undefined || !Number.isFinite(value)) return undefined;
    return Math.round(value);
  }

  private async safeGetActiveSession(): Promise<{ sessionId: string | null }> {
    if (!this.isPluginAvailable()) return { sessionId: null };
    try {
      const nativeState = await ChargingSessionLive.getActiveSession();
      console.log('[ChargingLive] getActiveSession OK', JSON.stringify(nativeState));
      return nativeState;
    } catch (error) {
      console.warn('[ChargingLive] getActiveSession failed', error);
      return { sessionId: null };
    }
  }

  private async safeStop(sessionId: string): Promise<void> {
    if (!this.isPluginAvailable()) return;
    try {
      console.log('[ChargingLive] stop native', JSON.stringify({ sessionId }));
      await ChargingSessionLive.stop({ sessionId, reason: 'completed' });
      console.log('[ChargingLive] stop OK', JSON.stringify({ sessionId }));
    } catch (error) {
      console.warn('[ChargingLive] Unable to stop native live session', error);
    }
  }

  private isPluginAvailable(): boolean {
    return Capacitor.isPluginAvailable('ChargingSessionLive');
  }

  private describeSession(session: ISession): Record<string, unknown> {
    return {
      id: session.id,
      state: session.state,
      innerState: session.innerState,
      startedAt: session.startedAt,
      stoppedAt: session.stoppedAt,
      endedAt: session.endedAt,
      energy: session.energy,
      power: session.power,
      totalCost: session.totalCost,
      soc: session.soc,
    };
  }

  private describePayload(payload: StartChargingSessionOptions & UpdateChargingSessionOptions): Record<string, unknown> {
    return {
      sessionId: payload.sessionId,
      title: payload.title,
      language: payload.language,
      stateKey: payload.stateKey,
      startedAt: payload.startedAt,
      kwh: payload.kwh,
      powerKw: payload.powerKw,
      estimatedCost: payload.estimatedCost,
      currency: payload.currency,
      socPercent: payload.socPercent,
      elapsedSeconds: payload.elapsedSeconds,
      occupationSeconds: payload.occupationSeconds,
      gracePeriodSeconds: payload.gracePeriodSeconds,
      reservationSeconds: payload.reservationSeconds,
      canStop: payload.canStop,
    };
  }

  private async reportLiveActivityPushToken(pushToken: string, sessionId: string): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios' || !pushToken || !sessionId) return;

    const clientId = this.mainStateService.getClientId();
    if (!clientId) {
      console.warn('[ChargingLive] reportLiveActivityPushToken skipped: missing clientId');
      return;
    }

    const dto: Partial<IClient> = {
      wpEndpoint: {
        platformType: this.mainStateService.getPlatformType(),
        supportsLiveActivity: true,
        liveActivityPushToken: pushToken,
        liveActivitySessionId: sessionId,
        liveActivityUpdatedAt: new Date().toISOString(),
      },
    };

    await this.HIO.patchItem<IClient, Partial<IClient>>('client', clientId, dto);
    console.log('[ChargingLive] live activity push token registered', JSON.stringify({
      clientId,
      sessionId,
      tokenLength: pushToken.length,
    }));
  }
}
